import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchUserNotifications, 
  markNotificationAsRead,
  selectUserNotifications
} from '../../store/slices/notificationSlice';
import { 
  HiOutlineBell, 
  HiOutlineExclamationCircle,
  HiOutlineRefresh
} from 'react-icons/hi';
import './Notifications.scss';

const Notifications = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { loading, error } = useSelector(state => state.notifications);
  // Use selector to get notifications filtered by user email
  const filteredNotifications = useSelector(state => 
    selectUserNotifications(state, user?.email)
  );
  
  // Fetch notifications when component mounts
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchUserNotifications({ email: user.email }));
    }
  }, [dispatch, user?.email]);
  
  const handleRefresh = () => {
    if (user?.email) {
      dispatch(fetchUserNotifications({ email: user.email }));
    }
  };
  
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      dispatch(markNotificationAsRead({ 
        notificationId: notification.id,
        userEmail: user.email
      }));
    }
    
    if (notification.actionLink) {
      window.location.href = notification.actionLink;
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Group notifications by date
  const groupNotificationsByDate = () => {
    const groups = {};
    
    filteredNotifications.forEach(notification => {
      const date = notification.createdAt?.toDate?.() || new Date(notification.createdAt);
      const dateKey = date.toLocaleDateString();
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      
      groups[dateKey].push(notification);
    });
    
    return groups;
  };
  
  const groupedNotifications = groupNotificationsByDate();
  const dateGroups = Object.keys(groupedNotifications).sort((a, b) => {
    return new Date(b) - new Date(a);
  });
  
  return (
    <div className="notifications-page">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <HiOutlineBell className="header-icon" />
            Notifications
          </h1>
        </div>
        
        <div className="header-right">
          <button className="refresh-button" onClick={handleRefresh}>
            <HiOutlineRefresh />
            <span>Refresh</span>
          </button>
        </div>
      </div>
      
      <div className="notifications-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <HiOutlineExclamationCircle className="error-icon" />
            <p>{error}</p>
            <button onClick={handleRefresh}>Try Again</button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlineBell size={48} />
            </div>
            <h2>No Notifications</h2>
            <p>You don't have any notifications yet.</p>
          </div>
        ) : (
          <div className="notifications-list-container">
            {dateGroups.map(dateKey => (
              <div key={dateKey} className="date-group">
                <div className="date-header">
                  {new Date(dateKey).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                
                <div className="notifications-group">
                  {groupedNotifications[dateKey].map(notification => (
                    <div 
                      key={notification.id} 
                      className={`notification-item ${!notification.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon">
                        <HiOutlineBell />
                      </div>
                      
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">
                          {formatTime(notification.createdAt)}
                        </div>
                      </div>
                      
                      {!notification.read && (
                        <div className="unread-indicator"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications; 